import { Form, Spin, Table, Tooltip } from "antd";
import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import MaterialIcon from "material-icons-react";
import { downloadExcel } from "../../features/global/globalSlice";
import { cashConverter, dateForHumans, numberWithCommas } from "../../utils";
import TableLoading from "../../components/TableLoading";

export default function PaidInOut({
  fetchTableData,
  setPageIndex,
  setPageSize,
  pageSize,
  pageIndex,
  formData,
}) {
  const [form] = Form.useForm();
  const formRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const url = "api/v1/collections/fetch-dashboard";
  const { data, count, loading } = useSelector((state) => ({
    data: state.fetch.data[url] || [],
    count: state.fetch.count[url] || 0,
    loading: state.fetch.loading[url],
  }));

  const { glbLoading } = useSelector((state) => state.global);

  const columns = [
    {
      title: "Organization",
      dataIndex: "orgDesc",
      render: (item) => {
        return <div>{item}</div>;
      },
      sorter: (a, b) => a.orgDesc.localeCompare(b.orgDesc),
    },
    {
      title: "Last Seen",
      dataIndex: "lastSeen",
      render: (item) => {
        return <div>{dateForHumans(item)}</div>;
      },
      sorter: (a, b) => a.lastSeen.localeCompare(b.lastSeen),
    },
    {
      title: "Paid-In",
      dataIndex: "paidIn",
      render: (item) => {
        return <div>{item}</div>;
      },
      sorter: (a, b) => a.paidIn.localeCompare(b.paidIn),
    },

    {
      title: "Paid-Out",
      dataIndex: "paidOut",
      render: (item) => {
        return <div>{item}</div>;
      },
      sorter: (a, b) => a.paidOut.localeCompare(b.paidOut),
    },
    {
      title: "Paid-Out Error",
      dataIndex: "paidOutError",
      render: (item) => {
        return <div>{item}</div>;
      },
      sorter: (a, b) => a.paidOutError.localeCompare(b.paidOutError),
    },
    {
      title: "Commission",
      dataIndex: "commission",
      render: (item) => {
        return <div>{item}</div>;
      },
      sorter: (a, b) => a.commission.localeCompare(b.commission),
    },
    {
      title: "Wallet Amount",
      dataIndex: "walAmount",
      render: (item) => {
        return <div>{item}</div>;
      },
      sorter: (a, b) => a.walAmount.localeCompare(b.walAmount),
    },
    {
      title: "isLoss",
      dataIndex: "isLoss",
      render: (item) => {
        return (
          <div
            className="text-[#FFF] inline-block rounded-[5px] px-2 py-1 w-auto"
            style={{
              backgroundColor: item ? "#F44336" : "#28A745",
            }}
          >
            {item ? "True" : "False"}
          </div>
        );
      },
      sorter: (a, b) => (a.isLoss === b.isLoss ? 0 : a.isLoss ? -1 : 1),
    },

    {
      title: "Loss",
      dataIndex: "loss",
      render: (item, record) => {
        return (
          <div
            className="text-[#FFF] inline-block rounded-[5px] px-2 py-1 w-auto"
            style={{
              backgroundColor: record?.isLoss ? "#F44336" : "#28A745",
            }}
          >
            {item}
          </div>
        );
      },
      sorter: (a, b) => (a.loss || 0) - (b.loss || 0),
    },
  ];

  const handleDownload = async (item) => {
    const res = await dispatch(
      downloadExcel({
        url: "api/v1/collections/export-transaction-summary",
      })
    );
    if (res?.payload) {
      const blobData = res.payload.data;
      const headers = res.payload.headers;
      const contentType = headers["content-type"] || "application/octet-stream";
      const contentDisposition = headers["content-disposition"];
      let fileName = "paidIn_paidOut";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }
      const blob = new Blob([blobData], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      await toast.success(`${fileName} downloaded successfully`);
    } else {
      toast.error("Failed to download file");
    }
  };

  if (loading) return <TableLoading />;
  return (
    <>
      <div className="bg-white rounded-[10px] mt-[1.25rem] h-full">
        <div className="flex flex-col w-full lg:px-10 px-3 ">
          <div className="flex lg:flex-row flex-col gap-3 justify-between item-center mt-[2rem]">
            <h3 className="heading_4 ">Payment Grid</h3>
            {data?.grid?.length > 0 && (
              <div className="flex justify-end item-center">
                {glbLoading ? (
                  <Spin className="sms-spin" />
                ) : (
                  <Tooltip placement="top" title={"Download Excel"}>
                    <button
                      onClick={handleDownload}
                      className="flex items-center"
                    >
                      <MaterialIcon size={45} color="#00B050" icon="article" />
                      <span>Export to excel</span>
                    </button>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
          <div className="mb-5">
            <section>
              <Table
                rowSelection={false}
                className="mt-[1.31rem] w-full"
                scroll={{
                  x: "auto",
                }}
                rowKey={(record) => record?.blkUid}
                columns={columns}
                dataSource={data?.grid}
                loading={loading}
                pagination={{
                  position: ["bottomCenter"],
                  current: pageIndex + 1,
                  total: count,
                  pageSize: pageSize,
                  onChange: (page, size) => {
                    setPageIndex(page - 1);
                    setPageSize(size);
                    fetchTableData(page - 1, size);
                  },
                  showSizeChanger: false,
                  hideOnSinglePage: true,
                }}
              />
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
